import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ArticleTranslationPreview from '@/pages/demo/multilingual/components/ArticleTranslationPreview';
import ReaderPreferences from '@/pages/demo/multilingual/components/ReaderPreferences';
import InclusiveCoverage from '@/pages/demo/multilingual/components/InclusiveCoverage';
import { PLANNED_LANGUAGES } from '@/lib/multilingual';

export default function MultilingualDemoPage() {
  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative bg-foreground-950 pt-28 md:pt-40 pb-14 md:pb-20 px-4 md:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-foreground-950 to-foreground-950"></div>
          <div className="relative">
            <div className="flex flex-wrap items-center gap-3">
              <p className="gs-rise font-label uppercase tracking-[0.25em] text-sm text-accent-400 font-bold">Multilingual &amp; inclusive publishing</p>
              <span className="gs-rise inline-flex items-center gap-1.5 bg-accent-500 text-foreground-950 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-flask-line"></i></span>
                Preview
              </span>
            </div>
            <h1 className="gs-rise-2 font-heading font-extrabold uppercase text-background-50 text-4xl md:text-7xl leading-[0.94] tracking-tight mt-3 max-w-4xl">
              GreenSide in more than one language
            </h1>
            <p className="gs-rise-3 mt-5 text-background-200 text-base md:text-lg max-w-2xl">
              A front-end concept for multilingual publishing and inclusive community coverage. Translations are prewritten samples — no translation service runs and nothing is saved.
            </p>
            <div className="gs-rise-3 mt-7 flex flex-wrap gap-3">
              <a href="#article-preview" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-foreground-950 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line"></i></span>
                See a translated article
              </a>
              <Link to="/demo-admin" className="inline-flex items-center gap-2 border border-background-50/30 hover:border-background-50 text-background-50 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-dashboard-3-line"></i></span>
                Multilingual workflow
              </Link>
            </div>
          </div>
        </section>

        {/* Preview disclaimer */}
        <div className="bg-accent-100 border-b border-accent-300 px-4 md:px-8 py-2.5 flex items-start gap-2.5">
          <span className="w-4 h-4 flex items-center justify-center text-accent-800 shrink-0 mt-0.5"><i className="ri-alert-line"></i></span>
          <p className="text-xs md:text-sm text-accent-900 max-w-4xl">
            <strong className="font-semibold">Multilingual preview — not the whole site.</strong> Only the selected sample sections on this page (and the language used in the header selector) are translated. Every other page remains in English for now.
          </p>
        </div>

        {/* Article translation preview */}
        <section id="article-preview" className="bg-background-50 px-4 md:px-8 py-14 md:py-20 scroll-mt-24">
          <ArticleTranslationPreview />
        </section>

        {/* Reader preferences */}
        <section id="preferences" className="bg-background-100 border-y border-background-200 px-4 md:px-8 py-14 md:py-20 scroll-mt-24">
          <ReaderPreferences />
        </section>

        {/* Inclusive coverage */}
        <section id="coverage" className="bg-background-50 px-4 md:px-8 py-14 md:py-20 scroll-mt-24">
          <InclusiveCoverage />
        </section>

        {/* Roadmap / scope note */}
        <section className="bg-background-100 border-t border-background-200 px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Where this is going</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground-950 leading-[1.05] mt-3">English and Spanish first</h2>
              <p className="mt-4 text-foreground-600">
                This concept starts with English and Spanish. Additional languages are shown as a clearly-labelled roadmap only — they are not translated in this preview.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500">Language roadmap</p>
                <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <li className="flex items-center gap-2 text-sm text-foreground-800"><span className="w-4 h-4 flex items-center justify-center text-primary-600"><i className="ri-checkbox-circle-fill"></i></span>English <span className="text-[11px] text-foreground-500">live</span></li>
                  <li className="flex items-center gap-2 text-sm text-foreground-800"><span className="w-4 h-4 flex items-center justify-center text-primary-600"><i className="ri-checkbox-circle-fill"></i></span>Spanish <span className="text-[11px] text-foreground-500">live</span></li>
                  {PLANNED_LANGUAGES.map((l) => (
                    <li key={l} className="flex items-center gap-2 text-sm text-foreground-500"><span className="w-4 h-4 flex items-center justify-center text-foreground-400"><i className="ri-time-line"></i></span>{l} <span className="text-[11px]">planned</span></li>
                  ))}
                </ul>
                <div className="mt-5 border-t border-background-200 pt-4 space-y-2.5 text-sm text-foreground-700">
                  <p className="flex items-start gap-2"><span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0 text-foreground-400"><i className="ri-close-circle-line"></i></span>No production AI translation or paid translation API is connected.</p>
                  <p className="flex items-start gap-2"><span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0 text-foreground-400"><i className="ri-close-circle-line"></i></span>No subscriber data is read or changed, and no demographic information is collected.</p>
                  <p className="flex items-start gap-2"><span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0 text-foreground-400"><i className="ri-close-circle-line"></i></span>Nothing is published automatically.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}